import { cn } from "@/common/helpers/utils";
import { useDevice } from "@/common/hooks/use-device";
import {
  ColumnDef,
  ColumnFiltersState,
  RowData,
  RowSelectionState,
  SortingState,
  useTable,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Import types and helpers
import { filterVisibleColumns } from "./_helpers/filter-visible-columns";
import { RowWithIdentifier } from "./_helpers/row-identifier";
import { DataTableHeightMode, DataTableProps } from "./types";

// Import components
import { createActionsColumn } from "./_components/actions-column";
import { DataTableContent } from "./_components/data-table-content";
import { DataTableFooter } from "./_components/data-table-footer";
import { DataTableHeader } from "./_components/data-table-header";
import { createExpansionColumn } from "./_components/expansion-column";
import { createSelectionColumn } from "./_components/selection-column";
import { dataTableFeatures, type DataTableFeatureSet } from "./data-table-features";
import { DataTableRow } from "./types";

export function DataTable<TData extends RowData & RowWithIdentifier = RowData & RowWithIdentifier, TValue = unknown>({
  className,
  columns,
  customSelectionActions,
  data = [],
  features = {
    expansion: false,
    multiSelection: false,
    pagination: true,
    search: true,
  },
  filters = [],
  filterValues = {},
  defaultFilterValues = {},
  footerComponent,
  headerComponent,
  filterContainerClassName,
  filterContainerCustomContent,
  filterContainerCustomContentPosition,
  initialSorting,
  isError = false,
  isLoading = false,
  meta,
  paginationState,
  rowSelection,
  searchPlaceholder = "Search...",
  showClearSelection,
  showSelectionCount,
  selection = [],
  selectionMode = "uuid",
  identifierProperty = "uuid",
  serverSidePagination = false,
  sortingMapping = {},
  sortColumns = [],
  timestampSortFields = [],
  subComponent,
  tableBackground,
  tableClassName,
  totalItems,
  actions,
  rowActions,
  setRowSelection,
  heightMode = DataTableHeightMode.Viewport,
  getRowCanExpand,
  getRowCanSelect,
  groupByCallback,
  searchValue,
  SearchComponent,
  noDataTitle,
  noDataDescription,
  noDataIcon,
  noResultsTitle,
  noResultsDescription,
}: DataTableProps<TData, TValue>) {
  const { deviceType, isMobileOrTablet } = useDevice();

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting || []);
  const [searchTerm, setSearchTerm] = useState(searchValue ?? "");
  const [previousSearchValue, setPreviousSearchValue] = useState(searchValue);
  const pendingPageResetRef = useRef(false);
  const resetToFirstPageRef = useRef<() => void>(() => {});

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const committedSearchTerm = searchValue ?? debouncedSearchTerm;

  if (searchValue !== previousSearchValue) {
    setPreviousSearchValue(searchValue);
    if (searchValue !== undefined) {
      setSearchTerm(searchValue);
    }
  }

  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  useEffect(() => {
    actionsRef.current?.onSearchChange?.(debouncedSearchTerm);

    if (pendingPageResetRef.current) {
      pendingPageResetRef.current = false;
      resetToFirstPageRef.current();
    }
  }, [debouncedSearchTerm]);

  const visibleColumns = useMemo(() => filterVisibleColumns(columns, deviceType), [columns, deviceType]);

  const isSortingEnabled =
    sortColumns.length > 0 ||
    timestampSortFields.length > 0 ||
    visibleColumns.some((col) => ("enableSorting" in col ? col.enableSorting : false));
  const isSelectionEnabled = !!(setRowSelection || actions?.onSelectionChange || actions?.onClearSelection);

  const internalRowSelection = useMemo(() => {
    if (rowSelection !== undefined) return rowSelection;
    if (isSelectionEnabled && selection.length > 0) {
      if (selectionMode === "uuid") {
        return (selection as string[]).reduce((selectionState, id) => {
          if (id) {
            selectionState[id] = true;
          }
          return selectionState;
        }, {} as RowSelectionState);
      } else {
        return (selection as TData[]).reduce((selectionState, item) => {
          const id = item[identifierProperty];
          if (id) {
            selectionState[id] = true;
          }
          return selectionState;
        }, {} as RowSelectionState);
      }
    }
    return {};
  }, [rowSelection, selection, isSelectionEnabled, selectionMode, identifierProperty]);

  const selectionColumn = useMemo(
    () => (isSelectionEnabled ? [createSelectionColumn<TData, TValue>()] : []),
    [isSelectionEnabled],
  );

  const expansionColumn = useMemo(
    () => (features.expansion && !features.hideExpansionButton ? [createExpansionColumn<TData, TValue>()] : []),
    [features.expansion, features.hideExpansionButton],
  );

  const actionsColumn = useMemo(() => {
    if (!rowActions || isMobileOrTablet) return [];

    const anyRowHasActions = data.some((row) => rowActions({ original: row } as DataTableRow<TData>).length > 0);

    return anyRowHasActions ? [createActionsColumn<TData, TValue>(rowActions)] : [];
  }, [rowActions, isMobileOrTablet, data]);

  const tableColumns = useMemo(() => {
    return [...expansionColumn, ...selectionColumn, ...visibleColumns, ...actionsColumn] as ColumnDef<
      DataTableFeatureSet,
      TData,
      unknown
    >[];
  }, [expansionColumn, selectionColumn, visibleColumns, actionsColumn]);

  const table = useTable<DataTableFeatureSet, TData>({
    features: dataTableFeatures,
    data: data as TData[],
    columns: tableColumns,
    enableRowSelection: isSelectionEnabled && (getRowCanSelect ? (row) => getRowCanSelect(row.original) : true),
    enableMultiRowSelection: isSelectionEnabled && features.multiSelection,
    enableRowRangeSelection: false,
    enableSorting: isSortingEnabled,
    enableColumnFilters: filters.length > 0,
    enableExpanding: features.expansion,
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
    ...(isSortingEnabled && {
      manualSorting: !!actions?.onSortingChange,
      onSortingChange: (updater) => {
        const newSorting = typeof updater === "function" ? updater(sorting) : updater;
        setSorting(newSorting);

        const mappedSorting: Array<{ field: string; direction: "asc" | "desc" }> = [];
        for (const sort of newSorting) {
          const mappedFields = sortingMapping[sort.id];
          if (mappedFields && mappedFields.length > 0) {
            for (const field of mappedFields) {
              mappedSorting.push({
                field: field,
                direction: sort.desc ? "desc" : "asc",
              });
            }
          } else {
            mappedSorting.push({
              field: sort.id,
              direction: sort.desc ? "desc" : "asc",
            });
          }
        }

        actions?.onSortingChange?.(mappedSorting);
      },
    }),
    ...(filters.length > 0 && {
      onColumnFiltersChange: setColumnFilters,
    }),
    ...(features.expansion && {
      getRowCanExpand: getRowCanExpand ? (row) => getRowCanExpand(row.original) : undefined,
      getIsRowExpanded: features.hideExpansionButton
        ? (row) => Boolean(row.original[identifierProperty]) && (getRowCanExpand ? getRowCanExpand(row.original) : true)
        : undefined,
    }),
    getRowId: (row: TData) => row[identifierProperty],
    manualPagination: serverSidePagination || !features.pagination,
    pageCount: totalItems && features.pagination ? Math.ceil(totalItems / paginationState.pageSize) : undefined,
    initialState: {
      ...(features.pagination && {
        pagination: {
          pageIndex: paginationState.page - 1,
          pageSize: paginationState.pageSize,
        },
      }),
      sorting: initialSorting || [],
    },
    state: {
      ...(isSortingEnabled && { sorting }),
      ...(filters.length > 0 && { columnFilters }),
      ...(isSelectionEnabled && { rowSelection: internalRowSelection }),
      ...(features.pagination && {
        pagination: {
          pageIndex: paginationState.page - 1,
          pageSize: paginationState.pageSize,
        },
      }),
    },
    meta: {
      ...meta,
      ...actions,
      sortingMapping,
    },
    onPaginationChange: features.pagination
      ? (updater) => {
          const newPagination =
            typeof updater === "function"
              ? updater({ pageIndex: paginationState.page - 1, pageSize: paginationState.pageSize })
              : updater;

          const newState = {
            page: newPagination.pageIndex + 1,
            pageSize: newPagination.pageSize,
          };

          const isUnchanged = newState.page === paginationState.page && newState.pageSize === paginationState.pageSize;
          if (isUnchanged) {
            return;
          }

          actions?.onPaginationChange?.(newState);
        }
      : undefined,
    onRowSelectionChange: (updater) => {
      if (setRowSelection) {
        setRowSelection(updater);
        return;
      }

      if (!actions?.onSelectionChange) return;

      const newSelection = typeof updater === "function" ? updater(internalRowSelection) : updater;
      const selectedIds = Object.keys(newSelection).filter((key) => newSelection[key]);

      if (!features.multiSelection && selectedIds.length > 1) {
        const lastSelectedId = selectedIds[selectedIds.length - 1];

        if (selectionMode === "uuid") {
          actions.onSelectionChange([lastSelectedId]);
          return;
        }

        const selectedObject = (data as TData[]).find((item) => item[identifierProperty] === lastSelectedId);
        if (selectedObject) {
          actions.onSelectionChange([selectedObject]);
        }
        return;
      }

      if (selectionMode === "uuid") {
        actions.onSelectionChange(selectedIds);
        return;
      }

      const currentPageIds = new Set((data as TData[]).map((item) => (item as unknown)[identifierProperty]));

      const selectedObjectsOnCurrentPage = selectedIds
        .map((id) => (data as TData[]).find((item) => (item as unknown)[identifierProperty] === id))
        .filter((item): item is TData => item !== undefined);

      const selectedIdsNotOnCurrentPage = selectedIds.filter((id) => !currentPageIds.has(id));

      const objectsFromOtherPages = (selection as TData[]).filter((item) =>
        selectedIdsNotOnCurrentPage.includes((item as unknown)[identifierProperty]),
      );

      actions.onSelectionChange([...objectsFromOtherPages, ...selectedObjectsOnCurrentPage]);
    },
  });

  const selectedCount = Object.keys(internalRowSelection).filter((key) => internalRowSelection[key]).length;

  const resetToFirstPage = useCallback(() => {
    if (!features.pagination) return;

    if (paginationState.page === 1) {
      table.setPageIndex(0);
      return;
    }

    table.setPageIndex(0);
    actions?.onPaginationChange?.({ page: 1, pageSize: paginationState.pageSize });
  }, [features.pagination, table, paginationState, actions]);

  resetToFirstPageRef.current = resetToFirstPage;

  const handleSearchChange = (value: string) => {
    if (value === searchTerm) {
      return;
    }
    setSearchTerm(value);
    pendingPageResetRef.current = true;
  };

  const handleFilterChange = useCallback(
    (filterId: string, values: string[]) => {
      actions?.onFilterChange?.(filterId, values);
      resetToFirstPage();
    },
    [actions, resetToFirstPage],
  );

  const hasNonDefaultFilters = useMemo(() => {
    for (const filter of filters) {
      const currentValues = filterValues[filter.id] || [];
      const defaultValues = defaultFilterValues[filter.id] || [];
      if (currentValues.length !== defaultValues.length) {
        return true;
      }
      if (JSON.stringify(currentValues) !== JSON.stringify(defaultValues)) {
        return true;
      }
    }
    return false;
  }, [filters, filterValues, defaultFilterValues]);

  const activeFilterCount = useMemo(
    () =>
      Object.values(filterValues).reduce((count, values) => count + values.filter((value) => value !== "").length, 0),
    [filterValues],
  );

  const hasDefaultFilters = useMemo(
    () => Object.values(defaultFilterValues).some((values) => values.length > 0),
    [defaultFilterValues],
  );

  const applyFilterValues = (getValues: (filterId: string) => string[]) => {
    if (actions?.onFilterChange) {
      for (const filter of filters) {
        actions.onFilterChange(filter.id, getValues(filter.id));
      }
    }
    resetToFirstPage();
  };

  const handleResetAllFilters = () => {
    applyFilterValues((filterId) => defaultFilterValues[filterId] ?? []);
  };

  const handleClearAllFilters = () => {
    applyFilterValues(() => []);
  };

  const handleClearSearch = () => {
    handleSearchChange("");
  };

  const clearAllFiltersAction = activeFilterCount > 0 && hasDefaultFilters ? handleClearAllFilters : undefined;
  const resetAllFiltersAction = hasNonDefaultFilters ? handleResetAllFilters : undefined;

  const getContainerClasses = () => {
    switch (heightMode) {
      case DataTableHeightMode.Viewport:
        return "flex flex-col flex-1 min-h-0";
      case DataTableHeightMode.Content:
        return "flex flex-col min-h-0";
      case DataTableHeightMode.Custom:
        return "flex flex-col h-full";
      default:
        return "flex flex-col h-full";
    }
  };

  return (
    <div className={cn(getContainerClasses(), className)}>
      <DataTableHeader
        tableBackground={tableBackground}
        headerComponent={headerComponent}
        filterContainerClassName={filterContainerClassName}
        filterContainerCustomContent={filterContainerCustomContent}
        filterContainerCustomContentPosition={filterContainerCustomContentPosition}
        features={features}
        filters={filters}
        activeFilterCount={activeFilterCount}
        hasDefaultFilters={hasDefaultFilters}
        hasNonDefaultFilters={hasNonDefaultFilters}
        searchTerm={searchTerm}
        searchPlaceholder={searchPlaceholder}
        table={table}
        sortColumns={sortColumns}
        timestampSortFields={timestampSortFields}
        serverSidePagination={serverSidePagination}
        totalItems={totalItems}
        isLoading={isLoading}
        onFilterChange={handleFilterChange}
        onClearAllFilters={clearAllFiltersAction}
        onResetAllFilters={resetAllFiltersAction}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        currentFilterValues={filterValues}
        SearchComponent={SearchComponent}
      />

      <DataTableContent
        table={table}
        data={data}
        isLoading={isLoading}
        isError={isError}
        activeFilterCount={activeFilterCount}
        hasDefaultFilters={hasDefaultFilters}
        hasNonDefaultFilters={hasNonDefaultFilters}
        searchTerm={committedSearchTerm}
        tableClassName={tableClassName}
        tableBackground={tableBackground}
        onRowClick={actions?.onRowClick}
        onRowNavigate={actions?.onRowNavigate}
        subComponent={subComponent}
        rowActions={rowActions}
        onClearAllFilters={clearAllFiltersAction}
        onResetAllFilters={resetAllFiltersAction}
        onClearSearch={handleClearSearch}
        groupByCallback={groupByCallback}
        emptyStateProps={{ noDataTitle, noDataDescription, noDataIcon, noResultsTitle, noResultsDescription }}
      />

      <DataTableFooter
        tableBackground={tableBackground}
        footerComponent={footerComponent}
        showClearSelection={showClearSelection}
        showSelectionCount={showSelectionCount}
        isSelectionEnabled={isSelectionEnabled}
        selectedCount={selectedCount}
        internalRowSelection={internalRowSelection}
        customSelectionActions={customSelectionActions}
        selection={selection}
        serverSidePagination={serverSidePagination}
        totalItems={totalItems}
        table={table}
        setRowSelection={setRowSelection}
        onClearSelection={actions?.onClearSelection}
      />
    </div>
  );
}
